/**
 * Mock implementation of the project service for testing
 */

import type {
  Project,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectQueryParams,
  ProjectStats,
  ProjectPhoto,
  BudgetReport,
  TaskBudgetReport,
  IntervenantBudgetReport,
  PaginatedResponse,
  ApiResponse
} from '@/types'

import {
  ProjectStatus,
  ProjectPriority
} from '@/types'

export class MockProjectService {
  private projects: Project[] = []
  private projectIdCounter: number = 1

  constructor() {
    // Initialize with some sample projects
    this.initializeMockData()
  }

  /**
   * Initialize the mock service with sample data
   */
  private initializeMockData(): void {
    const now = new Date()
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const oneMonthLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    const threeMonthsLater = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)

    // Initialize with some sample projects in French and home construction category
    this.projects = [
      {
        id: this.projectIdCounter++,
        name: 'Rénovation de Façade',
        description: 'Rénovation complète de la façade avec isolation thermique par l\'extérieur et revêtement moderne',
        status: 'active' as ProjectStatus,
        priority: 'high' as ProjectPriority,
        startDate: oneWeekAgo.toISOString().split('T')[0],
        endDate: oneMonthLater.toISOString().split('T')[0],
        clientName: 'Famille Dubois',
        projectManager: 'Pierre Martin',
        budget: 35000,
        category: 'Construction Résidentielle',
        location: 'Lyon',
        tags: 'rénovation,façade,isolation,résidentiel',
        createdAt: oneWeekAgo.toISOString(),
        updatedAt: new Date().toISOString(),
        // Add mapped fields for UI consistency
        creationDate: oneWeekAgo.toISOString(),
        estimatedStartDate: oneWeekAgo.toISOString().split('T')[0],
        estimatedEndDate: oneMonthLater.toISOString().split('T')[0]
      },
      {
        id: this.projectIdCounter++,
        name: 'Extension de Maison',
        description: 'Construction d\'une extension de 40m² comprenant une chambre et une salle de bain',
        status: 'planned' as ProjectStatus,
        priority: 'medium' as ProjectPriority,
        startDate: now.toISOString().split('T')[0],
        endDate: threeMonthsLater.toISOString().split('T')[0],
        clientName: 'Famille Moreau',
        projectManager: 'Sophie Leclerc',
        budget: 75000,
        category: 'Construction Résidentielle',
        location: 'Paris',
        tags: 'extension,chambre,agrandissement,maison',
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        // Add mapped fields for UI consistency
        creationDate: now.toISOString(),
        estimatedStartDate: now.toISOString().split('T')[0],
        estimatedEndDate: threeMonthsLater.toISOString().split('T')[0]
      },
      {
        id: this.projectIdCounter++,
        name: 'Réaménagement Intérieur',
        description: 'Reconfiguration complète de l\'espace intérieur pour optimiser la circulation et la luminosité',
        status: 'on_hold' as ProjectStatus,
        priority: 'critical' as ProjectPriority,
        startDate: oneWeekAgo.toISOString().split('T')[0],
        endDate: oneMonthLater.toISOString().split('T')[0],
        clientName: 'M. et Mme Bernard',
        projectManager: 'Michel Dupont',
        budget: 55000,
        category: 'Rénovation Intérieure',
        location: 'Bordeaux',
        tags: 'réaménagement,intérieur,espace,luminosité',
        createdAt: oneMonthLater.toISOString(),
        updatedAt: oneMonthLater.toISOString(),
        // Add mapped fields for UI consistency
        creationDate: oneMonthLater.toISOString(),
        estimatedStartDate: oneWeekAgo.toISOString().split('T')[0],
        estimatedEndDate: oneMonthLater.toISOString().split('T')[0]
      },
      {
        id: this.projectIdCounter++,
        name: 'Construction Écologique',
        description: 'Nouvelle construction d\'un pavillon familial avec matériaux écologiques et panneaux solaires',
        status: 'completed' as ProjectStatus,
        priority: 'low' as ProjectPriority,
        startDate: oneWeekAgo.toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        clientName: 'Famille Lambert',
        projectManager: 'Emma Rousseau',
        budget: 215000,
        category: 'Construction Écologique',
        location: 'Nantes',
        tags: 'écologique,solaire,matériaux,construction',
        createdAt: oneMonthLater.toISOString(),
        updatedAt: now.toISOString(),
        // Add mapped fields for UI consistency
        creationDate: oneMonthLater.toISOString(),
        estimatedStartDate: oneWeekAgo.toISOString().split('T')[0],
        estimatedEndDate: new Date().toISOString().split('T')[0]
      },
      {
        id: this.projectIdCounter++,
        name: 'Rénovation Salle de Bain',
        description: 'Modernisation complète de deux salles de bain avec installations de douches à l\'italienne',
        status: 'completed' as ProjectStatus, // Note: changed from 'cancelled' to match enum
        priority: 'medium' as ProjectPriority,
        startDate: oneWeekAgo.toISOString().split('T')[0],
        endDate: oneMonthLater.toISOString().split('T')[0],
        clientName: 'M. Petit',
        projectManager: 'Robert Leroy',
        budget: 28000,
        category: 'Rénovation Intérieure',
        location: 'Marseille',
        tags: 'salle de bain,douche,rénovation,plomberie',
        createdAt: threeMonthsLater.toISOString(),
        updatedAt: threeMonthsLater.toISOString(),
        // Add mapped fields for UI consistency
        creationDate: threeMonthsLater.toISOString(),
        estimatedStartDate: oneWeekAgo.toISOString().split('T')[0],
        estimatedEndDate: oneMonthLater.toISOString().split('T')[0]
      }
    ]

    // Initialize with 15 more random projects to demonstrate pagination - French home construction themed
    const statusOptions = [ProjectStatus.PLANNED, ProjectStatus.ACTIVE, ProjectStatus.ON_HOLD, ProjectStatus.COMPLETED]
    const priorityOptions = [ProjectPriority.LOW, ProjectPriority.MEDIUM, ProjectPriority.HIGH, ProjectPriority.CRITICAL]
    const categories = ['Construction Résidentielle', 'Rénovation Intérieure', 'Construction Écologique', 'Extension de Maison', 'Aménagement Extérieur', 'Rénovation Énergétique', 'Rénovation de Patrimoine']
    const projectManagers = ['Pierre Martin', 'Sophie Leclerc', 'Michel Dupont', 'Emma Rousseau', 'Robert Leroy', 'Céline Dubois', 'François Lefebvre']
    const clientNames = ['Famille Dubois', 'Famille Moreau', 'M. et Mme Bernard', 'Famille Lambert', 'M. Petit', 'Mme Girard', 'Famille Roux', 'SCI Les Oliviers']

    for (let i = 0; i < 15; i++) {
      const randomStatus = statusOptions[Math.floor(Math.random() * statusOptions.length)]
      const randomPriority = priorityOptions[Math.floor(Math.random() * priorityOptions.length)]
      const randomCategory = categories[Math.floor(Math.random() * categories.length)]
      const randomManager = projectManagers[Math.floor(Math.random() * projectManagers.length)]
      const randomClient = clientNames[Math.floor(Math.random() * clientNames.length)]

      const startDate = new Date(now.getTime() + (Math.random() * 60 - 30) * 24 * 60 * 60 * 1000)
      const endDate = new Date(startDate.getTime() + (30 + Math.random() * 60) * 24 * 60 * 60 * 1000)
      
      // Array of French home construction project names
      const projectNames = [
        'Rénovation de Cuisine',
        'Installation de Fenêtres',
        'Réfection de Toiture',
        'Isolation des Combles',
        'Construction de Véranda',
        'Aménagement de Jardin',
        'Installation Électrique',
        'Rénovation Appartement',
        'Piscine sur Mesure',
        'Extension en Bois',
        'Aménagement de Combles',
        'Rénovation de Cave',
        'Installation Domotique',
        'Réfection de Façade',
        'Création de Terrasse'
      ]

      // Get a project name based on the index
      const projectName = projectNames[i % projectNames.length]

      // French descriptions for home construction projects
      const descriptions = [
        'Rénovation complète avec installation de nouveaux équipements et finitions haut de gamme',
        'Modernisation des espaces avec optimisation de la luminosité et des volumes',
        'Travaux d\'amélioration énergétique et d\'isolation thermique pour plus de confort',
        'Restructuration complète de l\'espace pour une meilleure circulation',
        'Construction d\'extension en matériaux écologiques et durables',
        'Installation de solutions innovantes pour optimiser l\'espace de vie',
        'Réfection totale avec préservation des éléments de caractère'
      ]

      // Get a random description
      const description = descriptions[Math.floor(Math.random() * descriptions.length)]

      // French cities
      const cities = ['Paris', 'Lyon', 'Marseille', 'Bordeaux', 'Nantes', 'Strasbourg', 'Lille', 'Toulouse', 'Nice', 'Montpellier']
      const location = cities[Math.floor(Math.random() * cities.length)]
      
      const projectData: Project = {
        id: this.projectIdCounter++,
        name: projectName,
        description: description,
        status: randomStatus,
        priority: randomPriority,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        clientName: randomClient,
        projectManager: randomManager,
        budget: Math.floor(Math.random() * 150000) + 15000,
        category: randomCategory,
        location: location,
        tags: `${randomCategory.toLowerCase().replace(' ', '-')},renovation,construction,france`,
        createdAt: startDate.toISOString(),
        updatedAt: now.toISOString(),
        // For consistency with the project detail view, also add the mapped fields
        creationDate: startDate.toISOString(),
        estimatedStartDate: startDate.toISOString().split('T')[0],
        estimatedEndDate: endDate.toISOString().split('T')[0]
      }

      this.projects.push(projectData)
    }
  }

  /**
   * Helper to create a success API response
   */
  private createSuccessResponse<T>(data: T, message?: string): ApiResponse<T> {
    return {
      success: true,
      data,
      timestamp: new Date().toISOString(),
      message
    }
  }

  /**
   * Helper to create a paginated response
   */
  private paginateResults<T>(items: T[], page = 0, size = 10): PaginatedResponse<T> {
    const totalElements = items.length
    const totalPages = Math.ceil(totalElements / size)
    const start = page * size
    const end = Math.min(start + size, totalElements)
    const content = items.slice(start, end)

    // Return paginated response matching the PaginationMeta interface
    return {
      success: true,
      data: content,
      timestamp: new Date().toISOString(),
      meta: {
        page,
        limit: size,
        total: totalElements,
        totalPages,
        hasNext: page < totalPages - 1,
        hasPrev: page > 0
      }
    }
  }

  /**
   * Create a project filter function based on search parameters
   */
  private createFilterFunction(params: ProjectQueryParams): (project: Project) => boolean {
    return (project: Project) => {
      // Filter by search term (name)
      if (params.name && !project.name.toLowerCase().includes(params.name.toLowerCase())) {
        return false
      }

      // Filter by status
      if (params.status && project.status !== params.status) {
        return false
      }

      // Filter by priority
      if (params.priority && project.priority !== params.priority) {
        return false
      }

      // Filter by client name
      if (params.clientName && !project.clientName.toLowerCase().includes(params.clientName.toLowerCase())) {
        return false
      }

      // Filter by project manager
      if (params.projectManager && !project.projectManager.toLowerCase().includes(params.projectManager.toLowerCase())) {
        return false
      }

      // Filter by category
      if (params.category && !project.category.toLowerCase().includes(params.category.toLowerCase())) {
        return false
      }

      // Filter by tags
      if (params.tags) {
        const tagTerms = params.tags.toLowerCase().split(',').map(tag => tag.trim())
        const projectTags = project.tags.toLowerCase().split(',').map(tag => tag.trim())
        if (!tagTerms.some(tag => projectTags.includes(tag))) {
          return false
        }
      }

      // Filter by start date range
      if (params.startDateFrom && project.startDate < params.startDateFrom) {
        return false
      }
      if (params.startDateTo && project.startDate > params.startDateTo) {
        return false
      }

      // Filter by end date range
      if (params.endDateFrom && project.endDate < params.endDateFrom) {
        return false
      }
      if (params.endDateTo && project.endDate > params.endDateTo) {
        return false
      }

      return true
    }
  }

  /**
   * Sort projects based on sort parameter
   */
  private sortProjects(projects: Project[], sort = 'name,asc'): Project[] {
    const [field, direction] = sort.split(',')
    const sortOrder = direction === 'asc' ? 1 : -1

    return [...projects].sort((a, b) => {
      const aValue = (a as any)[field]
      const bValue = (b as any)[field]
      
      if (aValue < bValue) return -1 * sortOrder
      if (aValue > bValue) return 1 * sortOrder
      return 0
    })
  }

  /**
   * Calculate project statistics
   */
  private calculateProjectStats(): ProjectStats {
    const stats = {
      active: 0,
      planned: 0,
      onHold: 0,
      completed: 0,
      total: 0,
      totalBudget: 0,
      avgBudget: 0
    }

    this.projects.forEach(project => {
      switch (project.status) {
        case ProjectStatus.ACTIVE:
          stats.active++
          break
        case ProjectStatus.PLANNED:
          stats.planned++
          break
        case ProjectStatus.ON_HOLD:
          stats.onHold++
          break
        case ProjectStatus.COMPLETED:
          stats.completed++
          break
      }
      stats.totalBudget += project.budget
    })

    stats.total = this.projects.length
    stats.avgBudget = stats.total > 0 ? stats.totalBudget / stats.total : 0

    return stats
  }

  /**
   * Mock API method to get projects with search and filtering
   */
  async getProjects(params: ProjectQueryParams = {}): Promise<PaginatedResponse<Project>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 300))

    const { page = 0, size = 10, sort = 'name,asc', ...filterParams } = params

    // Apply filters
    let filteredProjects = this.projects
    if (Object.keys(filterParams).length > 0) {
      const filterFn = this.createFilterFunction(filterParams)
      filteredProjects = this.projects.filter(filterFn)
    }

    // Sort and then paginate results
    const sortedProjects = this.sortProjects(filteredProjects, sort)
    return this.paginateResults(sortedProjects, page, size)
  }

  /**
   * Mock API method to get project statistics
   */
  async getProjectStats(): Promise<ApiResponse<ProjectStats>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 200))

    return this.createSuccessResponse(this.calculateProjectStats())
  }

  /**
   * Mock API method to create a project
   */
  async createProject(projectData: ProjectCreateRequest): Promise<ApiResponse<Project>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 400))

    // Map the field names from the UI to the database field names
    const mappedData: any = { ...projectData }
    if (mappedData.creationDate) {
      mappedData.createdAt = mappedData.creationDate
      delete mappedData.creationDate
    }
    if (mappedData.estimatedStartDate) {
      mappedData.startDate = mappedData.estimatedStartDate
      delete mappedData.estimatedStartDate
    }
    if (mappedData.estimatedEndDate) {
      mappedData.endDate = mappedData.estimatedEndDate
      delete mappedData.estimatedEndDate
    }

    const newProject: Project = {
      ...mappedData,
      id: this.projectIdCounter++,
      createdAt: mappedData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Ensure required fields have defaults
      status: mappedData.status || ProjectStatus.DRAFT,
      priority: mappedData.priority || ProjectPriority.MEDIUM,
      startDate: mappedData.startDate || '',
      endDate: mappedData.endDate || '',
      clientName: mappedData.clientName || '',
      projectManager: mappedData.projectManager || '',
      budget: mappedData.budget || 0,
      category: mappedData.category || '',
      location: mappedData.location || '',
      tags: mappedData.tags || '',
      creationDate: mappedData.createdAt || new Date().toISOString(),
      estimatedStartDate: mappedData.startDate || '',
      estimatedEndDate: mappedData.endDate || ''
    }

    this.projects.push(newProject)

    // When returning to the UI, add back the mapped fields for consistency
    return this.createSuccessResponse({
      ...newProject,
      creationDate: newProject.createdAt as string,
      estimatedStartDate: newProject.startDate,
      estimatedEndDate: newProject.endDate
    })
  }

  /**
   * Mock API method to update a project
   */
  async updateProject(projectId: string | number, projectData: ProjectUpdateRequest): Promise<ApiResponse<Project>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 350))

    const index = this.projects.findIndex(p => p.id === Number(projectId))

    if (index === -1) {
      throw new Error('Project not found')
    }

    // Map the field names from the UI to the database field names
    const mappedData: any = { ...projectData }
    if (mappedData.creationDate) {
      mappedData.createdAt = mappedData.creationDate
      delete mappedData.creationDate
    }
    if (mappedData.estimatedStartDate) {
      mappedData.startDate = mappedData.estimatedStartDate
      delete mappedData.estimatedStartDate
    }
    if (mappedData.estimatedEndDate) {
      mappedData.endDate = mappedData.estimatedEndDate
      delete mappedData.estimatedEndDate
    }

    const updatedProject: Project = {
      ...this.projects[index],
      ...mappedData,
      id: Number(projectId),
      updatedAt: new Date().toISOString()
    }

    this.projects[index] = updatedProject

    // When returning to the UI, add back the mapped fields for consistency
    return this.createSuccessResponse({
      ...updatedProject,
      creationDate: updatedProject.createdAt as string,
      estimatedStartDate: updatedProject.startDate,
      estimatedEndDate: updatedProject.endDate
    })
  }

  /**
   * Mock API method to delete a project
   */
  async deleteProject(projectId: string | number): Promise<ApiResponse<{ success: boolean }>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 300))

    const index = this.projects.findIndex(p => p.id === Number(projectId))

    if (index === -1) {
      throw new Error('Project not found')
    }

    this.projects.splice(index, 1)
    return this.createSuccessResponse({ success: true })
  }

  /**
   * Mock API method to batch delete projects
   */
  async batchDeleteProjects(projectIds: (string | number)[]): Promise<ApiResponse<{ success: boolean; count: number }>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 500))

    const numericIds = projectIds.map(id => Number(id))
    this.projects = this.projects.filter(project => !numericIds.includes(project.id as number))

    return this.createSuccessResponse({ success: true, count: projectIds.length })
  }

  /**
   * Find project by ID helper
   */
  private findProjectById(projectId: string | number): Project | undefined {
    return this.projects.find(p => p.id === Number(projectId))
  }

  /**
   * Mock API method to get project by ID
   */
  async getProjectById(projectId: string | number): Promise<ApiResponse<Project>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 250))

    const project = this.findProjectById(projectId)

    if (!project) {
      throw new Error('Project not found')
    }

    // When returning to the UI, add back the mapped fields for consistency
    return this.createSuccessResponse({
      ...project,
      creationDate: project.createdAt as string,
      estimatedStartDate: project.startDate,
      estimatedEndDate: project.endDate
    })
  }

  /**
   * Mock API method to get project photos
   */
  async getProjectPhotos(projectId: string | number): Promise<ApiResponse<ProjectPhoto[]>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 300))

    const project = this.findProjectById(projectId)

    if (!project) {
      throw new Error('Project not found')
    }

    // Generate mock photos for the project
    const photos: ProjectPhoto[] = [
      {
        id: 'photo-1',
        projectId: Number(projectId),
        originalName: 'site-overview.jpg',
        fileName: `project-${projectId}-photo-1.jpg`,
        url: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80',
        size: 2456789,
        type: 'image/jpeg',
        uploadedAt: new Date().toISOString(),
        description: 'Site overview before construction',
        order: 1
      },
      {
        id: 'photo-2',
        projectId: Number(projectId),
        originalName: 'foundation-work.jpg',
        fileName: `project-${projectId}-photo-2.jpg`,
        url: 'https://images.unsplash.com/photo-1590725175161-be908fcb4c59?w=800&q=80',
        size: 1892345,
        type: 'image/jpeg',
        uploadedAt: new Date().toISOString(),
        description: 'Foundation preparation work',
        order: 2
      }
    ]

    return this.createSuccessResponse(photos)
  }

  /**
   * Mock API method to upload project photos
   */
  async uploadProjectPhotos(projectId: string | number, formData: FormData): Promise<ApiResponse<ProjectPhoto[]>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 800))

    const project = this.findProjectById(projectId)

    if (!project) {
      throw new Error('Project not found')
    }

    // Mock uploaded photo response
    const uploadedPhotos: ProjectPhoto[] = [
      {
        id: `photo-${Date.now()}`,
        projectId: Number(projectId),
        originalName: 'uploaded-photo.jpg',
        fileName: `project-${projectId}-${Date.now()}.jpg`,
        url: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80',
        size: 3145728,
        type: 'image/jpeg',
        uploadedAt: new Date().toISOString(),
        description: 'Newly uploaded photo',
        order: 99
      }
    ]

    return this.createSuccessResponse(uploadedPhotos)
  }

  /**
   * Mock API method to delete project photo
   */
  async deleteProjectPhoto(projectId: string | number, photoId: string): Promise<ApiResponse<{ success: boolean }>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 400))

    const project = this.findProjectById(projectId)

    if (!project) {
      throw new Error('Project not found')
    }

    return this.createSuccessResponse({ success: true })
  }

  /**
   * Mock API method to get budget consumption report
   */
  async getBudgetConsumptionReport(projectId: string | number): Promise<ApiResponse<BudgetReport>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 500))

    const project = this.findProjectById(projectId)

    if (!project) {
      throw new Error('Project not found')
    }

    const consumedBudget = project.budget * (0.3 + Math.random() * 0.6) // 30-90% consumed
    const remainingBudget = project.budget - consumedBudget

    const budgetReport: BudgetReport = {
      projectId: Number(projectId),
      projectName: project.name,
      totalBudget: project.budget,
      consumedBudget,
      remainingBudget,
      consumptionPercentage: (consumedBudget / project.budget) * 100,
      categories: [
        {
          name: 'Materials',
          allocated: project.budget * 0.4,
          spent: consumedBudget * 0.5,
          remaining: (project.budget * 0.4) - (consumedBudget * 0.5),
          percentage: 50
        },
        {
          name: 'Labor',
          allocated: project.budget * 0.35,
          spent: consumedBudget * 0.35,
          remaining: (project.budget * 0.35) - (consumedBudget * 0.35),
          percentage: 35
        },
        {
          name: 'Equipment',
          allocated: project.budget * 0.15,
          spent: consumedBudget * 0.1,
          remaining: (project.budget * 0.15) - (consumedBudget * 0.1),
          percentage: 10
        }
      ]
    }

    return this.createSuccessResponse(budgetReport)
  }

  /**
   * Mock API method to get task budget report
   */
  async getTaskBudgetReport(projectId: string | number): Promise<ApiResponse<TaskBudgetReport>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 400))

    const project = this.findProjectById(projectId)

    if (!project) {
      throw new Error('Project not found')
    }

    const taskBudgetReport: TaskBudgetReport = {
      projectId: Number(projectId),
      projectName: project.name,
      totalBudget: project.budget,
      tasks: [
        {
          taskId: 1,
          taskName: 'Site preparation',
          estimatedCost: project.budget * 0.1,
          actualCost: project.budget * 0.12,
          variance: project.budget * 0.02,
          status: 'completed'
        },
        {
          taskId: 2,
          taskName: 'Foundation work',
          estimatedCost: project.budget * 0.25,
          actualCost: project.budget * 0.23,
          variance: -project.budget * 0.02,
          status: 'completed'
        }
      ]
    }

    return this.createSuccessResponse(taskBudgetReport)
  }

  /**
   * Mock API method to get intervenant budget report
   */
  async getIntervenantBudgetReport(projectId: string | number): Promise<ApiResponse<IntervenantBudgetReport>> {
    // Create a delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 450))

    const project = this.findProjectById(projectId)

    if (!project) {
      throw new Error('Project not found')
    }

    const intervenantBudgetReport: IntervenantBudgetReport = {
      projectId: Number(projectId),
      projectName: project.name,
      totalBudget: project.budget,
      intervenants: [
        {
          intervenantId: 1,
          name: project.projectManager,
          role: 'Project Manager',
          totalCost: project.budget * 0.15,
          hours: 200,
          hourlyRate: (project.budget * 0.15) / 200
        },
        {
          intervenantId: 2,
          name: 'Construction Team',
          role: 'Construction Workers',
          totalCost: project.budget * 0.4,
          hours: 800,
          hourlyRate: (project.budget * 0.4) / 800
        }
      ]
    }

    return this.createSuccessResponse(intervenantBudgetReport)
  }
}

// Create and export an instance
export const mockProjectService = new MockProjectService()
export default mockProjectService
